// ─── BUNDEL PRODUK — helper MURNI, tanpa I/O ────────────────────────────────
//
// Rancangan & putusan pemilik: docs/RENCANA-bundling-produk.md (LANJUT
// 2026-10-21: K1 Ready Stock + Pre-Order sekaligus, K3 potongan menumpuk di
// atas promo, K4 nota Ready Stock utuh, K5 voucher boleh di atas bundel).
//
// Bundel BUKAN produk: tiap komponen dipesan sebagai baris `order_items` biasa
// di pesanan produknya, dan harga bundel DIALOKASIKAN ke baris-baris itu.
// Berkas ini SATU-SATUNYA tempat rumus bundel hidup di TS; SQL (RPC
// `checkout_orders_with_bundles`) memverifikasi angka yang sama dengan fungsi
// cermin yang ditulis bilangan bulat:
//   • `bundleLevelFixedPrice`  ↔ `bundle_fixed_price_for(...)`
//   • `bundleBasePrice`        ↔ blok mode di `bundle_expected_base(...)`
//   • `allocateBundle`         ↔ `voucher_allocate_lines` (DIPAKAI ULANG — alokasi
//                                 largest remainder yang sama dengan voucher)
//   • `bundleRatioAfterVoucher`↔ `bundle_round_ratio(B × X, S)`
//
// Tiga aturan uang yang dikunci tes (`bundle.test.ts`):
//   1. Σ harga baris = harga bundel PERSIS (bilangan bulat, largest remainder).
//   2. Bundel hanya BERLAKU bila lebih murah dari harga terpisah pembeli itu
//      (aturan termurah, cermin `never_worse`), dan tiap baris ≤ harga
//      terpisahnya — pembatalan sebagian apa pun tak pernah membuat pembeli
//      membayar lebih dari harga satuan.
//   3. Potongan bundel hanya memotong DASAR; HARGA TAMBAHAN varian (mig 7900)
//      ditempel utuh sesudahnya (PUTUSAN-HARGA-TAMBAHAN D2).

import { allocateVoucher } from "@/lib/voucher";
import { PROMO_GENERAL_SLOT } from "@/lib/promo";
import type { CustomerGroup } from "@/lib/reseller-groups";

// ─── Tipe ────────────────────────────────────────────────────────────────────

export type BundlePriceMode = "tetap" | "persen" | "nominal";
export type BundleProductType = "preorder" | "ready_stock";

export const BUNDLE_PRICE_MODES: readonly BundlePriceMode[] = ["tetap", "persen", "nominal"];
export const BUNDLE_MIN_COMPONENTS = 2;
export const BUNDLE_MAX_COMPONENTS = 10;

export type BundleComponent = {
  id: string;
  position: number;
  /** NULL = produknya terhapus (FK set null) → bundel "Komponen hilang". */
  productId: string | null;
  label: string | null;
  /** `variant_options.id` yang boleh; kosong = semua varian. */
  allowedOptionIds: string[];
};

export type Bundle = {
  id: string;
  storeId: string;
  name: string;
  slug: string;
  description: string | null;
  coverImageUrl: string | null;
  productType: BundleProductType;
  priceMode: BundlePriceMode;
  /** Mode `tetap`: harga Umum per 1 bundel, TANPA tambahan. */
  fixedPrice: number | null;
  /** Mode `persen` (0 < x < 100) atau `nominal` (Rp per bundel). */
  discountValue: number | null;
  /** Mode `tetap`: harga khusus per slot level (1..15). */
  levelPrices: Record<number, number>;
  /** Siapa yang berhak — kosong = semua; 0 = pelanggan umum. */
  levelSlots: number[];
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  components: BundleComponent[];
};

/** Harga TERPISAH satu komponen untuk pembeli ini (keluaran `sellableSelectionPrice`). */
export type BundleComponentPrice = {
  componentId: string;
  /** Harga satuan termasuk tambahan; `undefined` = harga menyusul. */
  price: number | undefined;
  /** Tambahan yang termasuk di `price` (≥ 0). */
  priceExtra: number;
};

export type BundleLinePrice = {
  componentId: string;
  /** Harga terpisah per pcs (termasuk tambahan) — kolom `bundle_base_unit_price`. */
  baseUnitPrice: number;
  /** Tambahan per pcs — kolom `unit_price_extra`. */
  priceExtra: number;
  /** Harga satuan baris bundel = alokasi dasar + tambahan — kolom `unit_price`. */
  unitPrice: number;
};

export type BundleNotApplicableReason =
  | "harga_menyusul"
  | "komponen_tidak_lengkap"
  | "tidak_lebih_hemat"
  | "harga_tidak_sah";

export type BundleQuote =
  | {
      applies: true;
      /** Σ dasar terpisah (tanpa tambahan) per 1 bundel. */
      separateBase: number;
      /** Σ tambahan per 1 bundel. */
      extrasTotal: number;
      /** Harga terpisah per 1 bundel (= separateBase + extrasTotal). */
      separateTotal: number;
      /** Harga bundel per 1 bundel TANPA tambahan — kolom `order_bundles.bundle_price`. */
      bundleBase: number;
      /** Yang dibayar per 1 bundel (= bundleBase + extrasTotal). */
      bundleTotal: number;
      /** Hemat per 1 bundel. */
      saving: number;
      lines: BundleLinePrice[];
    }
  | {
      applies: false;
      reason: BundleNotApplicableReason;
      /** Harga terpisah bila diketahui (dipajang sebagai harga yang dibayar). */
      separateTotal: number | null;
      /** Σ tambahan per 1 bundel bila diketahui — bagian dari `separateTotal` (bughunt D15-26). */
      extrasTotal?: number;
    };

// ─── Baris DB → tipe ─────────────────────────────────────────────────────────

/**
 * Kolom `bundles` + embed komponen & harga level — SATU string untuk pembaca
 * storefront (anon, REST server) dan admin, supaya mustahil menyimpang.
 * Literal tunggal (bukan gabungan `+`) agar tipe supabase-js tetap terurai.
 */
export const BUNDLE_SELECT =
  "id, store_id, name, slug, description, cover_image_url, product_type, price_mode, fixed_price, discount_value, level_slots, is_active, starts_at, ends_at, bundle_components(id, position, product_id, label, allowed_option_ids), bundle_level_prices(level_slot, price)";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapBundleRow(row: any): Bundle {
  const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const levelPrices: Record<number, number> = {};
  for (const lp of (row?.bundle_level_prices as unknown[]) ?? []) {
    const slot = num((lp as { level_slot?: unknown })?.level_slot);
    const p = num((lp as { price?: unknown })?.price);
    if (slot !== null && p !== null && p > 0) levelPrices[slot] = p;
  }
  const components: BundleComponent[] = ((row?.bundle_components as unknown[]) ?? [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((c: any) => ({
      id: String(c.id),
      position: Number(c.position) || 0,
      productId: c.product_id ? String(c.product_id) : null,
      label: typeof c.label === "string" && c.label.trim() ? c.label.trim() : null,
      allowedOptionIds: Array.isArray(c.allowed_option_ids) ? c.allowed_option_ids.map(String) : []
    }))
    .sort((a, b) => a.position - b.position);
  return {
    id: String(row.id),
    storeId: String(row.store_id ?? ""),
    name: String(row.name ?? ""),
    slug: String(row.slug ?? ""),
    description: typeof row.description === "string" && row.description.trim() ? row.description : null,
    coverImageUrl:
      typeof row.cover_image_url === "string" && row.cover_image_url.trim() ? row.cover_image_url : null,
    productType: row.product_type === "preorder" ? "preorder" : "ready_stock",
    priceMode: (BUNDLE_PRICE_MODES as readonly string[]).includes(row.price_mode)
      ? (row.price_mode as BundlePriceMode)
      : "tetap",
    fixedPrice: num(row.fixed_price),
    discountValue: num(row.discount_value),
    levelPrices,
    levelSlots: Array.isArray(row.level_slots) ? row.level_slots.map(Number).filter(Number.isFinite) : [],
    isActive: row.is_active === true,
    startsAt: row.starts_at ?? null,
    endsAt: row.ends_at ?? null,
    components
  };
}

/** Id produk komponen yang masih ada (urut posisi, tanpa duplikat). */
export function bundleProductIds(bundle: Pick<Bundle, "components">): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of [...bundle.components].sort((a, b) => a.position - b.position)) {
    if (c.productId && !seen.has(c.productId)) {
      seen.add(c.productId);
      out.push(c.productId);
    }
  }
  return out;
}

/** Bundel yang MEMUAT produk ini (urut nama). */
export function bundlesForProduct<T extends Pick<Bundle, "components" | "name">>(
  bundles: readonly T[],
  productId: string
): T[] {
  return bundles
    .filter((b) => b.components.some((c) => c.productId === productId))
    .sort((a, b) => a.name.localeCompare(b.name, "id"));
}

// ─── Aritmetika bilangan bulat (cermin `numeric`) ────────────────────────────

const MICRO = BigInt(1_000_000);
const HUNDRED_MICRO = BigInt(100_000_000);

function micro(value: number): bigint {
  if (!Number.isFinite(value)) return BigInt(0);
  return BigInt(Math.round(value * 1_000_000));
}

function toBig(value: number): bigint {
  if (!Number.isFinite(value)) return BigInt(0);
  return BigInt(Math.trunc(value));
}

/** floor((2n + d) / 2d) — setengah-ke-atas untuk n ≥ 0, d > 0. Cermin `bundle_round_ratio`. */
export function roundHalfUpRatio(numer: bigint, den: bigint): number {
  if (den <= BigInt(0) || numer <= BigInt(0)) return 0;
  const two = BigInt(2);
  return Number((two * numer + den) / (two * den));
}

function isWholeRupiah(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n) && Number.isInteger(n) && n >= 0;
}

// ─── Kelayakan & periode ────────────────────────────────────────────────────

/** Bundel sedang berjalan (aktif + dalam periode). Periode dievaluasi saat baca, idiom promo. */
export function isBundleRunning(
  bundle: Pick<Bundle, "isActive" | "startsAt" | "endsAt">,
  now: Date
): boolean {
  if (!bundle.isActive) return false;
  const t = now.getTime();
  if (!Number.isFinite(t)) return false;
  const start = bundle.startsAt ? Date.parse(bundle.startsAt) : NaN;
  const end = bundle.endsAt ? Date.parse(bundle.endsAt) : NaN;
  if (Number.isFinite(start) && t < start) return false;
  if (Number.isFinite(end) && t > end) return false;
  return true;
}

/**
 * Berhakkah pembeli ber-slot ini? Cermin `promoAllowsLevel`: kosong = semua;
 * pembeli tanpa level (tamu, pelanggan umum, grup NONAKTIF) dinilai slot 0.
 */
export function bundleAllowsLevel(
  bundle: Pick<Bundle, "levelSlots">,
  slot: number | null | undefined
): boolean {
  const slots = bundle.levelSlots ?? [];
  if (!slots.length) return true;
  return slots.includes(slot ?? PROMO_GENERAL_SLOT);
}

/** Slot level pembeli untuk bundel — grup nonaktif = pelanggan umum (cermin `promo_customer_slot`). */
export function bundleCustomerSlot(group: CustomerGroup): number | null {
  if (!group || group.isActive === false) return null;
  return group.slot;
}

/**
 * Id batas varian komponen yang TAK LAGI ada di produk (opsi atau grupnya
 * sudah dihapus form produk — `bundle_components.allowed_option_ids` tak ikut
 * disentuh). `liveOptionIds` = seluruh id opsi produk itu yang masih hidup
 * (Set, atau peta opsi → grup). Urut kemunculan, tanpa duplikat.
 */
export function staleAllowedOptionIds(
  component: { allowedOptionIds?: readonly string[] | null },
  liveOptionIds: { has(id: string): boolean }
): string[] {
  const out: string[] = [];
  for (const id of component.allowedOptionIds ?? []) {
    if (!liveOptionIds.has(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Bolehkah varian (dinyatakan lewat `option_ids`-nya) mengisi komponen ini?
 * Untuk setiap grup opsi yang punya opsi terpilih di `allowedOptionIds`,
 * varian WAJIB memakai salah satunya; grup tanpa pilihan bebas. Kosong =
 * semua varian. `optionGroupOf` memetakan id opsi → id grup opsi produk itu.
 *
 * Opsi terpilih yang tak lagi ada di produk (opsinya, atau seluruh grupnya,
 * dihapus form produk) = GAGAL-TUTUP: tak satu varian pun lolos sampai admin
 * memilih ulang batasnya (mig 8260, bughunt G8-17). Dulu id itu diabaikan,
 * jadi grup yang SEMUA opsi terpilihnya terhapus berubah bebas diam-diam.
 * Cermin SQL `bundle_variant_fits`.
 */
export function variantFitsComponent(
  component: Pick<BundleComponent, "allowedOptionIds">,
  variantOptionIds: readonly string[],
  optionGroupOf: ReadonlyMap<string, string>
): boolean {
  const allowed = component.allowedOptionIds ?? [];
  if (!allowed.length) return true;
  const allowedByGroup = new Map<string, Set<string>>();
  for (const optionId of allowed) {
    const groupId = optionGroupOf.get(optionId);
    if (!groupId) return false;
    const set = allowedByGroup.get(groupId) ?? new Set<string>();
    set.add(optionId);
    allowedByGroup.set(groupId, set);
  }
  const chosenByGroup = new Map<string, string>();
  for (const optionId of variantOptionIds) {
    const groupId = optionGroupOf.get(optionId);
    if (groupId) chosenByGroup.set(groupId, optionId);
  }
  for (const [groupId, set] of allowedByGroup) {
    const chosen = chosenByGroup.get(groupId);
    if (!chosen || !set.has(chosen)) return false;
  }
  return true;
}

// ─── Harga ───────────────────────────────────────────────────────────────────

/**
 * Harga bundel mode `tetap` (TANPA tambahan) untuk pembeli ini — K6:
 * harga khusus slotnya bila ada, kalau tidak Harga Umum − diskon % RATA grup
 * (bukan aturan merek / override varian: satu bundel bisa memuat banyak merek).
 * Tamu / pelanggan umum / grup nonaktif → Harga Umum. Cermin SQL
 * `bundle_fixed_price_for`.
 */
export function bundleLevelFixedPrice(
  bundle: Pick<Bundle, "fixedPrice" | "levelPrices">,
  group: CustomerGroup
): number | null {
  const umum = bundle.fixedPrice;
  if (!isWholeRupiah(umum) || umum <= 0) return null;
  const slot = bundleCustomerSlot(group);
  if (slot === null || !group) return umum;
  const special = bundle.levelPrices?.[slot];
  if (isWholeRupiah(special) && special > 0) return special;
  const d = Number(group.discountPersen);
  if (!Number.isFinite(d) || d <= 0) return umum;
  const pct = micro(Math.min(100, d));
  return roundHalfUpRatio(toBig(umum) * (HUNDRED_MICRO - pct), HUNDRED_MICRO);
}

/**
 * Harga bundel TANPA tambahan untuk Σ dasar terpisah `separateBase`.
 * `null` = mode tak bisa dihitung (setelan tak sah).
 */
export function bundleBasePrice(
  bundle: Pick<Bundle, "priceMode" | "fixedPrice" | "discountValue" | "levelPrices">,
  group: CustomerGroup,
  separateBase: number
): number | null {
  if (!isWholeRupiah(separateBase)) return null;
  switch (bundle.priceMode) {
    case "tetap":
      return bundleLevelFixedPrice(bundle, group);
    case "persen": {
      const p = Number(bundle.discountValue);
      if (!Number.isFinite(p) || p <= 0 || p >= 100) return null;
      return roundHalfUpRatio(toBig(separateBase) * (HUNDRED_MICRO - micro(p)), HUNDRED_MICRO);
    }
    case "nominal": {
      const n = Number(bundle.discountValue);
      if (!isWholeRupiah(n) || n <= 0) return null;
      return separateBase - n;
    }
    default:
      return null;
  }
}

/**
 * Sebar `total` ke komponen pro-rata `weights` — LARGEST REMAINDER yang SAMA
 * dengan voucher (`allocateVoucher` ↔ SQL `voucher_allocate_lines`), seri
 * dimenangkan urutan input. Σ = total persis. Bobot wajib > 0.
 */
export function allocateBundle(
  total: number,
  weights: readonly { key: string; weight: number }[]
): Map<string, number> {
  const out = new Map<string, number>();
  const alloc = allocateVoucher(
    total,
    weights.map((w) => ({ key: w.key, base: w.weight, qty: 1 }))
  );
  for (const a of alloc) out.set(a.key, a.alloc);
  return out;
}

/**
 * Hitung bundel untuk pembeli ini. `prices` = harga TERPISAH tiap komponen
 * (urut `position`), tepat satu per komponen bundel.
 */
export function quoteBundle(
  bundle: Pick<Bundle, "priceMode" | "fixedPrice" | "discountValue" | "levelPrices" | "components">,
  group: CustomerGroup,
  prices: readonly BundleComponentPrice[]
): BundleQuote {
  const comps = [...bundle.components].sort((a, b) => a.position - b.position);
  const byId = new Map(prices.map((p) => [p.componentId, p]));
  if (comps.length < BUNDLE_MIN_COMPONENTS || comps.some((c) => !byId.has(c.id))) {
    return { applies: false, reason: "komponen_tidak_lengkap", separateTotal: null };
  }
  const rows = comps.map((c) => byId.get(c.id)!);
  if (rows.some((r) => r.price === undefined)) {
    return { applies: false, reason: "harga_menyusul", separateTotal: null };
  }
  let separateTotal = 0;
  let extrasTotal = 0;
  const weights: { key: string; weight: number }[] = [];
  for (const r of rows) {
    const price = r.price as number;
    const extra = isWholeRupiah(r.priceExtra) ? r.priceExtra : 0;
    if (!isWholeRupiah(price) || price - extra <= 0) {
      return { applies: false, reason: "harga_tidak_sah", separateTotal: null };
    }
    separateTotal += price;
    extrasTotal += extra;
    weights.push({ key: r.componentId, weight: price - extra });
  }
  const separateBase = separateTotal - extrasTotal;
  const bundleBase = bundleBasePrice(bundle, group, separateBase);
  if (bundleBase === null || bundleBase <= 0) {
    return { applies: false, reason: "harga_tidak_sah", separateTotal, extrasTotal };
  }
  if (bundleBase >= separateBase) {
    return { applies: false, reason: "tidak_lebih_hemat", separateTotal, extrasTotal };
  }
  const alloc = allocateBundle(bundleBase, weights);
  const lines: BundleLinePrice[] = rows.map((r) => {
    const extra = isWholeRupiah(r.priceExtra) ? r.priceExtra : 0;
    return {
      componentId: r.componentId,
      baseUnitPrice: r.price as number,
      priceExtra: extra,
      unitPrice: (alloc.get(r.componentId) ?? 0) + extra
    };
  });
  return {
    applies: true,
    separateBase,
    extrasTotal,
    separateTotal,
    bundleBase,
    bundleTotal: bundleBase + extrasTotal,
    saving: separateBase - bundleBase,
    lines
  };
}

/**
 * K5 — voucher di atas bundel. Mesin voucher menghitung harga terpisah
 * SESUDAH voucher per komponen (`ownAfterVoucher`, dasar tanpa tambahan);
 * langkah bundel lalu diterapkan sebagai RASIO yang dikunci dari harga tanpa
 * voucher: B' = round(bundleBase × Σ own' / separateBase), dialokasikan ulang
 * pro-rata own'. Akibatnya voucher persen memotong harga bundel dengan persen
 * yang sama. Cermin SQL di `checkout_orders_with_bundles`.
 */
export function bundleRatioAfterVoucher(
  bundleBase: number,
  separateBase: number,
  ownAfterVoucher: readonly { key: string; weight: number }[]
): { total: number; alloc: Map<string, number> } {
  const x = ownAfterVoucher.reduce((s, w) => s + Math.max(0, Math.trunc(w.weight)), 0);
  if (!isWholeRupiah(bundleBase) || !isWholeRupiah(separateBase) || separateBase <= 0 || x <= 0) {
    return { total: 0, alloc: new Map() };
  }
  const total = Math.min(x, roundHalfUpRatio(toBig(bundleBase) * toBig(x), toBig(separateBase)));
  return { total, alloc: allocateBundle(total, ownAfterVoucher) };
}

/**
 * K5 untuk SATU pembelian bundel: `lines` = harga terpisah SESUDAH voucher
 * per komponen (termasuk tambahan; baris yang tak layak voucher = harga
 * terpisahnya sendiri). Memulangkan harga satuan akhir per komponen
 * (alokasi + tambahan) — cermin blok K5 `checkout_orders_with_bundles`.
 *
 * mig 8090 (bughunt D15-32): komponen yang TAK layak voucher (`eligible:
 * false` + `unitWithoutVoucher` = harga bundelnya tanpa voucher) tetap pada
 * harga itu; sisa B′ dibagi ke komponen layak saja. Dulu semua komponen
 * dialokasi ulang → komponen tak layak bergeser ±1 rupiah tanpa jejak voucher.
 * `eligible` absen = layak (perilaku lama).
 */
export function applyBundleAfterVoucher(
  quote: { bundleBase: number; separateBase: number },
  lines: readonly {
    componentId: string;
    priceExtra: number;
    unitAfterVoucher: number;
    eligible?: boolean;
    unitWithoutVoucher?: number;
  }[]
): Map<string, number> {
  const extraOf = (l: { priceExtra: number }) => (isWholeRupiah(l.priceExtra) ? l.priceExtra : 0);
  const weights = lines.map((l) => ({
    key: l.componentId,
    weight: Math.max(0, l.unitAfterVoucher - extraOf(l))
  }));
  const kept = new Map<string, number>();
  for (const l of lines) {
    if (l.eligible === false && typeof l.unitWithoutVoucher === "number" && isWholeRupiah(l.unitWithoutVoucher)) {
      kept.set(l.componentId, l.unitWithoutVoucher);
    }
  }
  const { total, alloc: allocAll } = bundleRatioAfterVoucher(quote.bundleBase, quote.separateBase, weights);
  let alloc = allocAll;
  if (kept.size > 0) {
    const ne = lines.reduce((s, l) => (kept.has(l.componentId) ? s + Math.max(0, kept.get(l.componentId)! - extraOf(l)) : s), 0);
    const eligibleWeights = weights.filter((w) => !kept.has(w.key));
    const xe = eligibleWeights.reduce((s, w) => s + Math.max(0, Math.trunc(w.weight)), 0);
    alloc = allocateBundle(Math.max(0, Math.min(xe, total - ne)), eligibleWeights);
  }
  const out = new Map<string, number>();
  for (const l of lines) {
    const fixed = kept.get(l.componentId);
    out.set(l.componentId, fixed !== undefined ? fixed : (alloc.get(l.componentId) ?? 0) + extraOf(l));
  }
  return out;
}

// ─── Keadaan pembelian (diturunkan saat baca) ────────────────────────────────

export type OrderBundleState = "utuh" | "tak_utuh" | "batal";

/**
 * `utuh` = tiap komponen masih punya k pcs hidup; `batal` = nol pcs hidup di
 * semua komponen; selain itu `tak_utuh`. Cermin SQL `order_bundle_state`.
 * `livePcsByComponent` = Σ qty baris HIDUP (bukan cancelled) per komponen.
 */
export function orderBundleState(
  qty: number,
  componentIds: readonly string[],
  livePcsByComponent: ReadonlyMap<string, number>
): OrderBundleState {
  let anyLive = false;
  let complete = componentIds.length > 0;
  for (const id of componentIds) {
    const live = livePcsByComponent.get(id) ?? 0;
    if (live > 0) anyLive = true;
    if (live < qty) complete = false;
  }
  if (!anyLive) return "batal";
  return complete ? "utuh" : "tak_utuh";
}

/** Bagian potongan BUNDEL per pcs pada sebuah baris (≥ 0). */
export function bundleDiscountPerPcs(
  baseUnitPrice: number | null | undefined,
  unitPrice: number | null | undefined
): number {
  if (!Number.isFinite(baseUnitPrice) || !Number.isFinite(unitPrice)) return 0;
  return Math.max(0, Math.round((baseUnitPrice as number) - (unitPrice as number)));
}

// ─── Stok & jumlah maksimum ──────────────────────────────────────────────────

/**
 * Jumlah bundel maksimum yang bisa dipesan dari stok varian pilihan. Dua
 * komponen yang memilih varian SAMA butuh 2 pcs per bundel. `stock` null =
 * tak dibatasi (PO tanpa kuota). Hasil ≥ 0.
 */
export function maxBundleQty(
  choices: readonly { variantId: string; stock: number | null }[]
): number | null {
  const need = new Map<string, { perBundle: number; stock: number | null }>();
  for (const c of choices) {
    const cur = need.get(c.variantId);
    if (cur) cur.perBundle += 1;
    else need.set(c.variantId, { perBundle: 1, stock: c.stock });
  }
  let max: number | null = null;
  for (const { perBundle, stock } of need.values()) {
    if (stock === null || stock === undefined) continue;
    const n = Math.max(0, Math.floor(stock / perBundle));
    max = max === null ? n : Math.min(max, n);
  }
  return max;
}

// ─── Teks ─────────────────────────────────────────────────────────────────────

const rupiah = (n: number) => `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(n))}`;

/** Ringkasan cara harga untuk kartu admin, mis. "Harga tetap Rp 399.000". */
export function describeBundlePrice(
  bundle: Pick<Bundle, "priceMode" | "fixedPrice" | "discountValue" | "levelPrices">
): string {
  switch (bundle.priceMode) {
    case "tetap": {
      const extra = Object.keys(bundle.levelPrices ?? {}).length;
      const base = bundle.fixedPrice ? `Harga tetap ${rupiah(bundle.fixedPrice)}` : "Harga tetap";
      return extra ? `${base} · ${extra} harga level khusus` : base;
    }
    case "persen":
      return `Hemat ${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(
        Number(bundle.discountValue ?? 0)
      )}% dari harga level pembeli`;
    case "nominal":
      return `Hemat ${rupiah(Number(bundle.discountValue ?? 0))} per bundling`;
    default:
      return "";
  }
}

/** Kalimat untuk pembeli saat bundel tak berlaku baginya (null = berlaku). */
export function bundleNotApplicableText(reason: BundleNotApplicableReason): string {
  switch (reason) {
    case "tidak_lebih_hemat":
      return "Dengan harga level kamu, lebih hemat beli satuan.";
    case "harga_menyusul":
      return "Ada pilihan yang harganya belum tersedia — pilih varian lain.";
    case "komponen_tidak_lengkap":
      return "Pilih varian untuk setiap barang bundling.";
    case "harga_tidak_sah":
    default:
      return "Harga bundling ini belum bisa dihitung.";
  }
}

/**
 * Kalimat pembeli/admin untuk galat RPC bundel (mig 8040). `null` = bukan galat
 * bundel — pemanggil memakai pemeta galatnya sendiri (voucher, rpcError).
 */
export function bundleErrorMessage(raw: string | null | undefined): string | null {
  const msg = String(raw ?? "");
  if (msg.includes("BUNDLE_NONE_APPLIES")) {
    return "Bundling di keranjang tidak lebih hemat untuk akun ini, jadi barangnya dipesan dengan harga satuan.";
  }
  // Gerbang klien §6.4 (`createCheckoutOrdersWithBundles`): harga per bundel
  // naik dari yang dipajang kartu — belum ada pesanan yang dibuat.
  if (msg.includes("BUNDLE_PRICE_CHANGED")) {
    return "Harga bundling berubah — periksa total terbaru, lalu tekan Keep Produk lagi.";
  }
  const m = msg.match(/BUNDLE_INVALID:([a-z_]+)/);
  if (m) {
    switch (m[1]) {
      case "inactive":
        return "Bundling di keranjang sudah berakhir atau dinonaktifkan. Hapus bundlingnya, lalu checkout lagi.";
      case "level":
        return "Bundling di keranjang tidak berlaku untuk level akun ini. Hapus bundlingnya, lalu checkout lagi.";
      case "components":
      case "variant":
        return "Isi bundling di keranjang sudah berubah. Buka lagi halaman bundlingnya dan pilih ulang variannya.";
      case "price_unknown":
        return "Ada barang di bundling yang harganya belum tersedia. Pilih varian lain di halaman bundlingnya.";
      case "price_changed":
      case "inconsistent":
      default:
        return "Harga bundling berubah — keranjang memuat ulang harga terbaru. Periksa lagi, lalu checkout.";
    }
  }
  if (msg.includes("BUNDEL_NOTA_UTUH") || /harus (?:dinotakan|ditagihkan) bersama semua barangnya/.test(msg)) {
    // "Bundel" = kalimat SQL sebelum istilah layar diganti (2026-10-22).
    const hit = msg.match(/Bund(?:el|ling) "[^"]*" harus (?:dinotakan|ditagihkan) bersama semua barangnya\./);
    return hit ? hit[0] : "Bundling harus ditagihkan bersama semua barangnya.";
  }
  if (/memuat (?:barang )?bund(?:el|ling)\b/.test(msg)) {
    return msg;
  }
  return null;
}

/** Persen hemat untuk badge (dibulatkan ke bawah, minimal 1 bila ada hemat). */
export function bundleSavingPercent(saving: number, separateBase: number): number {
  // Hemat dihitung atas DASAR (tambahan di luar potongan, D2), jadi pembaginya
  // pun Σ DASAR terpisah — bukan harga terpisah yang memuat tambahan (bughunt
  // D15-11: bundel 10 % ber-XXL tertulis "9 %"). Dibulatkan ke bawah, dengan
  // kelonggaran SETENGAH RUPIAH — batas pembulatan harga bundel ke rupiah utuh
  // (dasar 450.001 potongan 10 % = hemat 45.000 → 10 %, bukan 9 %). Kelonggaran
  // dalam poin persen (versi awal +0,05) ikut menaikkan harga tetap 180.100 dari
  // 200.000 (9,95 %) jadi "10 %" — tinjauan PR E.
  if (!(saving > 0) || !(separateBase > 0)) return 0;
  return Math.max(1, Math.floor(((saving + 0.5) * 100) / separateBase));
}
